const RESELLER_API_URL = `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/reseller-api`;

export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthenticationError";
  }
}

async function callResellerApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${RESELLER_API_URL}/${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
  });

  if (response.status === 401 || response.status === 403) {
    throw new AuthenticationError("Please log in to access reseller features");
  }

  const data = await response.text();

  if (!response.ok) {
    throw new Error(data || "API request failed");
  }
  return JSON.parse(data);
}

// Types
export interface ResellerProfile {
  id: string;
  traccar_user_id: number;
  company_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  stripe_account_id: string | null;
  stripe_onboarding_complete: boolean;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  reseller_id: string;
  traccar_user_id: number | null;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  town: string | null;
  state: string | null;
  postal_code: string | null;
  tax_id: string | null;
  customer_type: "company" | "person";
  status: "active" | "inactive" | "suspended";
  notes: string | null;
  stripe_customer_id: string | null;
  created_at: string;
  updated_at: string;
  vehicles?: Vehicle[];
}

export interface Vehicle {
  id: string;
  reseller_id: string;
  customer_id: string | null;
  traccar_device_id: number | null;
  name: string | null;
  plate_number: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  model_year: number | null;
  vin: string | null;
  color: string | null;
  status: "enabled" | "disabled";
  notes: string | null;
  created_at: string;
  updated_at: string;
  customer?: { id: string; name: string } | null;
}

export interface SimCard {
  id: string;
  reseller_id: string;
  vehicle_id: string | null;
  iccid: string;
  phone_number: string | null;
  carrier: string | null;
  apn: string | null;
  data_plan: string | null;
  status: "active" | "inactive" | "suspended";
  activation_date: string | null;
  expiry_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  vehicle?: { id: string; plate_number: string } | null;
}

export interface Contract {
  id: string;
  reseller_id: string;
  customer_id: string;
  name: string;
  description: string | null;
  billing_cycle: "monthly" | "quarterly" | "yearly";
  amount: number;
  currency: string;
  start_date: string;
  end_date: string | null;
  next_billing_date: string | null;
  status: "active" | "pending" | "cancelled" | "expired";
  auto_renew: boolean;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  created_at: string;
  updated_at: string;
  customer?: { id: string; name: string } | null;
}

export interface Invoice {
  id: string;
  number: string | null;
  customer_id: string | null;
  customer_name: string | null;
  amount: number;
  currency: string;
  status: "draft" | "open" | "paid" | "uncollectible" | "void";
  due_date: string | null;
  hosted_invoice_url: string | null;
  stripe_dashboard_url: string;
  created: string;
}

export interface Technician {
  id: string;
  reseller_id: string;
  traccar_user_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  is_available: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldService {
  id: string;
  reseller_id: string;
  customer_id: string;
  vehicle_id: string | null;
  technician_id: string | null;
  service_type: "installation" | "repair" | "maintenance" | "removal" | "inspection";
  status: "scheduled" | "in_progress" | "completed" | "cancelled";
  scheduled_date: string | null;
  completed_date: string | null;
  description: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  customer?: { id: string; name: string } | null;
  vehicle?: { id: string; plate_number: string } | null;
  technician?: { id: string; name: string } | null;
}

export interface ServiceHistory {
  id: string;
  field_service_id: string;
  vehicle_id: string;
  technician_id: string | null;
  service_type: string;
  description: string;
  parts_used: string | null;
  labor_hours: number | null;
  cost: number | null;
  completed_at: string;
  notes: string | null;
  created_at: string;
  vehicle?: { id: string; plate_number: string };
  technician?: { id: string; name: string } | null;
}

export interface DashboardStats {
  customers: number;
  vehicles: number;
  activeContracts: number;
  pendingServices: number;
  totalRevenue: number;
}

export interface StockLocation {
  id: string;
  reseller_id: string;
  name: string;
  address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeviceStock {
  id: string;
  reseller_id: string;
  serial_number?: string | null;
  imei: string | null;
  model: string;
  manufacturer: string | null;
  supplier: string | null;
  purchase_date: string | null;
  purchase_price: number | null;
  warranty_expiry: string | null;
  status: "in_stock" | "deployed" | "returned" | "maintenance" | "retired";
  location_id: string | null;
  traccar_device_id: number | null;
  customer_id: string | null;
  deployed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  sim_card_id: string | null;
  customer?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
  sim_card?: { id: string; iccid: string; phone_number: string | null } | null;
}

export interface DeviceMerge {
  id: string;
  reseller_id: string;
  primary_device_id: number;
  secondary_device_id: number;
  display_name: string | null;
  created_at: string;
}

// API Functions
export const fetchResellerProfile = () => callResellerApi<ResellerProfile>("profile");
export const updateResellerProfile = (data: Partial<ResellerProfile>) =>
  callResellerApi<ResellerProfile>("profile", { method: "PUT", body: JSON.stringify(data) });

export const fetchCustomers = () => callResellerApi<Customer[]>("customers");
export const fetchCustomer = (id: string) => callResellerApi<Customer>(`customers/${id}`);
export const createCustomer = (data: Partial<Customer>) =>
  callResellerApi<Customer>("customers", { method: "POST", body: JSON.stringify(data) });
export const updateCustomer = (id: string, data: Partial<Customer>) =>
  callResellerApi<Customer>(`customers/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteCustomer = (id: string) =>
  callResellerApi<{ success: boolean }>(`customers/${id}`, { method: "DELETE" });

export interface PagedResult<T> { data: T[]; total: number; }
export const fetchSimCards = () => callResellerApi<SimCard[]>("sim-cards");
export const fetchSimCardsPaged = (params: { page: number; limit?: number; search?: string }) => {
  const q = new URLSearchParams({ page: String(params.page), limit: String(params.limit ?? 50), search: params.search ?? "" });
  return callResellerApi<PagedResult<SimCard>>(`sim-cards?${q}`);
};
export const fetchSimCard = (id: string) => callResellerApi<SimCard>(`sim-cards/${id}`);
export const createSimCard = (data: Partial<SimCard>) =>
  callResellerApi<SimCard>("sim-cards", { method: "POST", body: JSON.stringify(data) });
export const updateSimCard = (id: string, data: Partial<SimCard>) =>
  callResellerApi<SimCard>(`sim-cards/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteSimCard = (id: string) =>
  callResellerApi<{ success: boolean }>(`sim-cards/${id}`, { method: "DELETE" });
export const deleteAllSimCards = () =>
  callResellerApi<{ success: boolean }>("sim-cards", { method: "DELETE" });
export const importSimCards = (rows: { icc: string; msisdn: string; imei: string; apn: string; carrier: string }[]) =>
  callResellerApi<{ sim_cards: number; devices_created: number; devices_linked: number }>(
    "sim-cards/import",
    { method: "POST", body: JSON.stringify({ rows }) }
  );

export const fetchContracts = () => callResellerApi<Contract[]>("contracts");
export const fetchContract = (id: string) => callResellerApi<Contract>(`contracts/${id}`);
export const createContract = (data: Partial<Contract>) =>
  callResellerApi<Contract>("contracts", { method: "POST", body: JSON.stringify(data) });
export const updateContract = (id: string, data: Partial<Contract>) =>
  callResellerApi<Contract>(`contracts/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteContract = (id: string) =>
  callResellerApi<{ success: boolean }>(`contracts/${id}`, { method: "DELETE" });

export const fetchInvoices = () => callResellerApi<Invoice[]>("invoices");
export const createInvoice = (data: { customer_id: string }) =>
  callResellerApi<Invoice>("invoices", { method: "POST", body: JSON.stringify(data) });
export const deleteInvoice = (id: string) =>
  callResellerApi<{ success: boolean }>(`invoices/${id}`, { method: "DELETE" });

export const fetchTechnicians = () => callResellerApi<Technician[]>("technicians");
export const fetchTechnician = (id: string) => callResellerApi<Technician>(`technicians/${id}`);
export const createTechnician = (data: Partial<Technician>) =>
  callResellerApi<Technician>("technicians", { method: "POST", body: JSON.stringify(data) });
export const updateTechnician = (id: string, data: Partial<Technician>) =>
  callResellerApi<Technician>(`technicians/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteTechnician = (id: string) =>
  callResellerApi<{ success: boolean }>(`technicians/${id}`, { method: "DELETE" });

export const fetchFieldServices = () => callResellerApi<FieldService[]>("field-services");
export const fetchFieldService = (id: string) => callResellerApi<FieldService>(`field-services/${id}`);
export const createFieldService = (data: Partial<FieldService>) =>
  callResellerApi<FieldService>("field-services", { method: "POST", body: JSON.stringify(data) });
export const updateFieldService = (id: string, data: Partial<FieldService>) =>
  callResellerApi<FieldService>(`field-services/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteFieldService = (id: string) =>
  callResellerApi<{ success: boolean }>(`field-services/${id}`, { method: "DELETE" });

export const fetchServiceHistory = (vehicleId?: string) =>
  callResellerApi<ServiceHistory[]>(`service-history${vehicleId ? `?vehicle_id=${vehicleId}` : ""}`);

export const fetchDashboardStats = () => callResellerApi<DashboardStats>("dashboard");

// Stock Locations
export const fetchStockLocations = () => callResellerApi<StockLocation[]>("stock-locations");
export const createStockLocation = (data: Partial<StockLocation>) =>
  callResellerApi<StockLocation>("stock-locations", { method: "POST", body: JSON.stringify(data) });
export const updateStockLocation = (id: string, data: Partial<StockLocation>) =>
  callResellerApi<StockLocation>(`stock-locations/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteStockLocation = (id: string) =>
  callResellerApi<{ success: boolean }>(`stock-locations/${id}`, { method: "DELETE" });

// Device Stock
export const fetchDeviceStock = () => callResellerApi<DeviceStock[]>("device-stock");
export const fetchDeviceStockPaged = (params: { page: number; limit?: number; search?: string; status?: string; sortBy?: string; sortDir?: "asc" | "desc"; traccarDeviceIds?: number[] }) => {
  const q = new URLSearchParams({ page: String(params.page), limit: String(params.limit ?? 50), search: params.search ?? "", status: params.status ?? "all", sort: params.sortBy ?? "created_at", dir: params.sortDir ?? "desc" });
  if (params.traccarDeviceIds?.length) q.set("traccar_ids", params.traccarDeviceIds.join(","));
  return callResellerApi<PagedResult<DeviceStock>>(`device-stock?${q}`);
};
export const fetchDeviceStockItem = (id: string) => callResellerApi<DeviceStock>(`device-stock/${id}`);
export const createDeviceStock = (data: Partial<DeviceStock>) =>
  callResellerApi<DeviceStock>("device-stock", { method: "POST", body: JSON.stringify(data) });
export const updateDeviceStock = (id: string, data: Partial<DeviceStock>) =>
  callResellerApi<DeviceStock>(`device-stock/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteDeviceStock = (id: string) =>
  callResellerApi<{ success: boolean }>(`device-stock/${id}`, { method: "DELETE" });
export const deployDeviceStock = (id: string, data: { traccar_device_id: number; customer_id: string }) =>
  callResellerApi<DeviceStock>(`device-stock/${id}/deploy`, { method: "POST", body: JSON.stringify(data) });
export const returnDeviceStock = (id: string, data?: { return_reason?: string }) =>
  callResellerApi<DeviceStock>(`device-stock/${id}/return`, { method: "POST", body: JSON.stringify(data || {}) });

export interface DeviceStockHistory {
  id: string;
  device_stock_id: string;
  action: string;
  previous_status: DeviceStock["status"] | null;
  new_status: DeviceStock["status"] | null;
  customer_id: string | null;
  traccar_device_id: number | null;
  notes: string | null;
  created_at: string;
  customer?: { id: string; name: string } | null;
}

export const fetchDeviceStockHistory = (id: string) =>
  callResellerApi<DeviceStockHistory[]>(`device-stock/${id}/history`);

export const initiateStripeOnboarding = (appUrl: string) =>
  callResellerApi<{ url: string }>("stripe-connect-onboard", {
    method: "POST",
    body: JSON.stringify({ app_url: appUrl, supabase_url: import.meta.env.VITE_SUPABASE_URL }),
  });

export const createBillingPortalSession = (customerId: string, returnUrl: string) =>
  callResellerApi<{ url: string }>("billing-portal", {
    method: "POST",
    body: JSON.stringify({ customer_id: customerId, return_url: returnUrl }),
  });

// Attachments
export interface Attachment {
  id: string;
  reseller_id: string;
  entity_type: string;
  entity_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  created_at: string;
}

export const fetchAttachments = (entityType: string, entityId: string) =>
  callResellerApi<Attachment[]>(`attachments?entity_type=${entityType}&entity_id=${entityId}`);

export const uploadAttachment = async (entityType: string, entityId: string, file: File): Promise<Attachment> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("entity_type", entityType);
  formData.append("entity_id", entityId);

  const response = await fetch(`${RESELLER_API_URL}/attachments`, {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });

  if (response.status === 401 || response.status === 403) {
    throw new AuthenticationError("Please log in to access reseller features");
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Failed to upload attachment");
  }
  return data;
};

export const deleteAttachment = (id: string) =>
  callResellerApi<{ success: boolean }>(`attachments/${id}`, { method: "DELETE" });

export const getAttachmentDownloadUrl = (id: string) =>
  callResellerApi<{ url: string; attachment: Attachment }>(`attachments/${id}`);

export const fetchDeviceMerges = () => callResellerApi<DeviceMerge[]>("device-merges");

export interface ScheduledReport {
  id: string;
  reseller_id: string;
  name: string;
  report_type: string;
  device_id: number | null;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week: number | null;
  day_of_month: number | null;
  time_of_day: string;
  date_range: 'last_day' | 'last_week' | 'last_month' | 'last_30_days';
  recipients: string[];
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export const fetchScheduledReports = () => callResellerApi<ScheduledReport[]>("scheduled-reports");
export const createScheduledReport = (data: Partial<ScheduledReport>) =>
  callResellerApi<ScheduledReport>("scheduled-reports", { method: "POST", body: JSON.stringify(data) });
export const updateScheduledReport = (id: string, data: Partial<ScheduledReport>) =>
  callResellerApi<ScheduledReport>(`scheduled-reports/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteScheduledReport = (id: string) =>
  callResellerApi<{ success: boolean }>(`scheduled-reports/${id}`, { method: "DELETE" });

export interface ResellerAdminUser {
  id: string;
  reseller_id: string;
  traccar_user_id: number;
  created_at: string;
}

export const fetchResellerAdminUsers = () => callResellerApi<ResellerAdminUser[]>("reseller-admin-users");
export const addResellerAdminUser = (data: { traccar_user_id: number }) =>
  callResellerApi<ResellerAdminUser>("reseller-admin-users", { method: "POST", body: JSON.stringify(data) });
export const removeResellerAdminUser = (id: string) =>
  callResellerApi<{ success: boolean }>(`reseller-admin-users/${id}`, { method: "DELETE" });
export const createDeviceMerge = (data: { primary_device_id: number; secondary_device_id: number; display_name?: string }) =>
  callResellerApi<DeviceMerge>("device-merges", { method: "POST", body: JSON.stringify(data) });
export const deleteDeviceMerge = (id: string) =>
  callResellerApi<{ success: boolean }>(`device-merges/${id}`, { method: "DELETE" });
